import moment from 'moment';

import { MarvColor, MarvColorPrimary, MarvColorBlue, MarvColorGreen, MarvColorOrange, MarvColorRed } from '@marv/types';
import { MarvDateService } from '@marv/services';
import { 
    LogaProxy, Operation, Kit, MissionStatus, OperationProfil, StoreLocation, TypeOfMission, AdditionalTimes, 
    Cra, DailyTourPlanning, PlanningMissionPurpose, PearlsPlanningEntry, OperationCustomerPlanningSetting,
    MerchImplementationOption 
} from "@app/models";
import { 
    MissionItem, MissionItemRetailer, MissionItemStore, MissionItemArea, MissionItemSector, 
    MissionItemOperation, MissionItemSalesRep 
} from '@app/components';
import { OperationService, CustomerFeedbackService } from '@app/services';

export class ActivityWorker {
    workerId: number;
    workerName: string;
    workerMobilePhone: string;
    workerEmail: string;
}

export class Activity {
    id: number;
    day: number;
    amStartTime: Date|null;
    amEndTime: Date|null;
    pmStartTime: Date|null;
    pmEndTime: Date|null;
    recoCompliance: boolean;
    comment: string;
    
    earliestStartMin: number|null;
    latestStartMin: number|null;
    requestedDurationMin: number|null;
    
    declaredPresence: boolean;
    confirmedPresence: boolean;
    reportable: boolean;
    reportingCompleted: boolean;
    
    worker: ActivityWorker|null = null;
    
    static FromJson(a: any) {
        let act: Activity = new Activity();
        act.id = a.id;
        act.day = a.day;
        act.recoCompliance = a.recoCompliance;
        act.comment = a.comment;
        act.amStartTime = (a.amStartTime !== null ? new Date(a.amStartTime) : null);
        act.amEndTime = (a.amEndTime != null ? new Date(a.amEndTime) : null);
        act.pmStartTime = (a.pmStartTime != null ? new Date(a.pmStartTime) : null);
        act.pmEndTime = (a.pmEndTime != null ? new Date(a.pmEndTime) : null);
        act.earliestStartMin = a.earliestStartMin;
        act.latestStartMin = a.latestStartMin;
        act.requestedDurationMin = a.requestedDurationMin;
        act.declaredPresence = (a.declaredPresence === 'YES');
        act.confirmedPresence = (a.confirmedPresence === 'YES');
        act.reportable = a.reportable;
        act.reportingCompleted = (a.reportingStatus === 'COMPLETE');
        
        if(a.worker){
            act.worker = {
                workerId: a.worker.workerId,
                workerName: a.worker.workerName,
                workerMobilePhone: a.worker.workerMobilePhone,
                workerEmail: a.worker.workerEmail
            }            
        }
        
        return act;
    }
}

export class AssignedWorkerDetails {
    workerId: number;
    workerName: string;
    workerMobilePhone: string;
    workerEmail: string;
}

export class MissionAssignedBudgetQuotaDto {
    /**"code of the Operation Profile Template**/
    tplCode: string;
    /**amount of quota assigned**/
    amount: number;
    /** details about the amount **/
    comment: string;
    /** date of computation of the quota**/
    computeDate: Date;
}

export class MissionCustomerCancellationDetails {
    author: string;
    date: Date;
    comments: string;
    missionStatusAtRequest: string;
}

export class MissionBriefDocument {
    sourceFileName: string;
    mimeType: string;
}

export class Order {

    id: number;
    missionCode: string;
    status: MissionStatus;
    createDate: Date;
    suiviReco: boolean;
    operation: Operation;
    location: StoreLocation;
    kit: Kit;
    activities: Activity[];
    csAuthor: string;
    profil: OperationProfil;
    workerIdsBeingOfferedMission: Array<number>;
    typeOfMission: TypeOfMission;
    worker: AssignedWorkerDetails;

    storeCustomerSectorName: string;
    storeCustomerSectorId: number;

    csPresence: boolean;
    appointment: string;
    appointmentTime: Date;

    customerDeletePermissionAllowed: boolean;
    customerDeleteRequestPermissionAllowed: boolean;
    customerEditPermissionAllowed: boolean;

    sharingCustomerSectorContactDetailsToWorker: boolean;

    storeContact: string;
    mobileStoreContact: string;
    storeAccess: string;

    comment: string;
    briefMissionDetails: string;
    briefDocument: MissionBriefDocument;

    dailyTourPlanning: DailyTourPlanning;

    // enum value GOOD, MEDIUM....
    customerFeedback: string;
    customerFeedbackComment: number;
    uiIncidentDeclared: boolean;

    additionalTimes: AdditionalTimes[] = [];

    presence: boolean;
    pcheckAlerts: string[] = [];

    cras: Cra[];

    storeAliasClientCode: string;
    storeAliasResourcePool: string;

    assignedQuota: MissionAssignedBudgetQuotaDto[] = [];

    /**
     * utitility flag to show/hide content with simple filtering
     */
    uiShouldHide: boolean;

    /**
     * ui flag to cache presence check status as human readable string, will not come from the API
     */
    uiPresenceCheckString: string;

    cancellationRequest: MissionCustomerCancellationDetails|null;

    plannedPurposes: PlanningMissionPurpose[];
    merchImplementationOption: MerchImplementationOption|null;

    static FromJson(json: any): Order {
        let o: Order = new Order();
                
        o.activities = []
        json['activities'].forEach( (ooo: any) => o.activities.push(Activity.FromJson(ooo)));
        
        o.id = json['missionId'];
        o.missionCode = json['missionCode'];
        o.operation = json['operation'];
        o.location = json['location'];

        o.csAuthor = json['csAuthor'];
        o.suiviReco = json['reco'];
        o.kit = json['kit'];
        o.profil = json['profil'];
        o.workerIdsBeingOfferedMission = json['workerIdsBeingOfferedMission'];
        
        let day: number  = (o.activities?.length > 0 ? o.activities[0].day : 0);
        o.status = MissionStatus.FromOrder(json['status'], day);

        o.storeCustomerSectorName = json['storeCustomerSectorName'];
        o.storeCustomerSectorId = json['storeCustomerSectorId'];


        o.typeOfMission = json['typeOfMission'];
        o.worker = json['worker'];
        o.createDate = json['createDate'];

        o.csPresence = json['csPresence'];
        o.appointment = json['appointment'];
        o.appointmentTime = json['appointmentTime'];
        o.sharingCustomerSectorContactDetailsToWorker = json['sharingCustomerSectorContactDetailsToWorker'];

        o.storeContact = json['storeContact'];
        o.mobileStoreContact = json['mobileStoreContact'];
        o.storeAccess = json['storeAccess'];

        o.comment = json['comment'];
        o.briefMissionDetails = json['briefMissionDetails'];
        o.briefDocument = json['briefDocument'];

        o.cras = json['workDaysReportable'];
        o.customerFeedback = json['customerFeedback'];
        o.customerFeedbackComment = json['customerFeedbackComment'];

        o.presence = json['presence'];
        o.pcheckAlerts = json['pcheckAlert'];

        o.customerDeletePermissionAllowed = json['customerDeletePermissionAllowed'];
        o.customerEditPermissionAllowed = json['customerEditPermissionAllowed'];
        o.customerDeleteRequestPermissionAllowed = json['customerDeleteRequestPermissionAllowed'];
        o.cancellationRequest = json['cancellationRequest'];

        o.storeAliasClientCode = json['storeAliasClientCode'];

        o.assignedQuota = json['assignedQuota'];

        o.dailyTourPlanning = json['dailyTourPlanning'];
        o.plannedPurposes = json['plannedPurposes'];
        o.merchImplementationOption = json['merchImplementationOption'];
        
        return o;
    }
    
    getMissionsItems(operationsSettings: Map<number, OperationCustomerPlanningSetting>): Array<MissionItem> {
        let today = MarvDateService.GetToday();
        let missionsItems: Array<MissionItem> = [];
        let operationSettings = operationsSettings.get(this.operation?.id); 
        for(let a of this.activities){
            let missionItem = new MissionItem();

            let missionItemRetailer = new MissionItemRetailer();
            missionItemRetailer.id = this.location?.retailer?.id;
            missionItemRetailer.name = this.location?.retailer?.name;

            let missionItemStore = new MissionItemStore();
            missionItemStore.name = this.location?.name;
            missionItemStore.administrativeArea = this.location?.address?.administrativeArea;
            missionItemStore.zipCode = this.location?.address?.zipCode;
            missionItemStore.city = this.location?.address?.city;

            let missionItemOperation = new MissionItemOperation();
            missionItemOperation.id = this.operation?.id;
            missionItemOperation.name = this.operation?.name;
            missionItemOperation.typeOfMission = this.typeOfMission?.code;
            missionItemOperation.source = 'loga';

            let missionItemArea = new MissionItemArea();
            missionItemArea.id = this.location?.customerArea;
            missionItemArea.name = this.location?.customerAreaName;

            let missionItemSector = new MissionItemSector();
            missionItemSector.id = this.location?.customerSector;
            missionItemSector.name =  this.location?.customerSectorName;

            missionItem.activityId = a.id;                                   
            missionItem.missionId = this.id;                                   
            missionItem.retailer = missionItemRetailer;
            missionItem.store = missionItemStore;
            missionItem.customerArea = missionItemArea;
            missionItem.customerSector = missionItemSector;
            missionItem.operation = missionItemOperation;
            missionItem.day = a.day;

            if (a.amStartTime && a.amEndTime) {
                missionItem.amStartTime = MarvDateService.ToTime(a.amStartTime);
                missionItem.amEndTime = MarvDateService.ToTime(a.amEndTime);
            }

            if (a.pmStartTime && a.pmEndTime) {
                missionItem.pmStartTime = MarvDateService.ToTime(a.pmStartTime);
                missionItem.pmEndTime = MarvDateService.ToTime(a.pmEndTime);
            }
            
            if(a.worker){
                missionItem.worker = {
                    id: a.worker.workerId,
                    name: a.worker.workerName,
                    email: a.worker.workerEmail,
                    phone: a.worker.workerMobilePhone
                };
            } 

            let report = this.cras.find(r => r.activity === a.id);
            if(report){
                missionItem.report = {
                    id: report.id,
                    date: report.date,
                    published: report.published,
                    status: report.status,
                    activityId: report.activity
                };
            }

            missionItem.duration = (a.requestedDurationMin ? MarvDateService.MinutesToStr(a.requestedDurationMin, 'h') : MarvDateService.GetMissionDuration(a.amStartTime, a.amEndTime, a.pmStartTime, a.pmEndTime));
            missionItem.presenceConfirmed = (this.presence ? this.presence : a.confirmedPresence);
            missionItem.reportingCompleted = (a.reportingCompleted ? a.reportingCompleted : this.status.code === MissionStatus.Status.FULFILLED);
            missionItem.reportable = a.reportable;
            missionItem.status = (!missionItem.worker ? new MissionStatus(MissionStatus.Status.PENDING_VALIDATION, a.day) : (a.day < today && (missionItem.presenceConfirmed || missionItem.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, a.day) : (a.day > today && this.status.code === MissionStatus.Status.IN_PROGRESS ? new MissionStatus(MissionStatus.Status.ASSIGNED, a.day) : this.status)));
            missionItem.profil = this.profil?.name;
            missionItem.quotaConsumed = this.assignedQuota?.reduce(
                (accumulator, currentValue) => accumulator + currentValue.amount,
                0,
            );
            
            let evaluationNote: number|null = null;
            if(this.customerFeedback != null){
                evaluationNote = CustomerFeedbackService.CustomerFeedBackValues.find(c => c.value === this.customerFeedback)?.rank || null;
            }
            missionItem.evaluationNote = evaluationNote;            
            missionItem.recoCompliance = a.recoCompliance;
                                        
            if(this.plannedPurposes?.length > 0){
                this.plannedPurposes.forEach(o => {
                    missionItem.purposes.push({
                        name: o.purposeName,
                        fulfilled: true
                    });
                });
            }
            
            missionItem.planningEntryType = 'STORECHECK';
            missionItem.affected = (this.worker?.workerId !== null && this.worker?.workerId !== undefined);
            missionItem.showPresenceCheckInfos = (operationSettings ? operationSettings.showPresenceCheckInfos : false);
            missionItem.source = 'loga';

            missionsItems.push(missionItem);        
        }
        return missionsItems;
    }

    getStartDate(): Date|null {
        let date = null;
        if (this.activities) {
            let activity = this.activities.reduce((prev, curr) =>
                prev.amStartTime != null && curr.amStartTime != null && prev.amStartTime.valueOf() < curr.amStartTime.valueOf() ? prev : curr);

            if (activity.amStartTime == null && activity.amEndTime == null) {
                activity = this.activities.reduce((prev, curr) => prev.pmStartTime != null && curr.pmStartTime != null && prev.pmStartTime.valueOf() < curr.pmStartTime.valueOf() ? prev : curr);
                date = activity.pmStartTime;
            } 
            else {
                date = activity.amStartTime;
            }
        }
        return date;
    }

    

}
